import { useEffect } from 'react'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { APPLICATION_COPY } from '@/locales/en'
import { APPLICATION_FLOW_STEP_IDS, type ApplicationFlowStepId } from '../../../applicationFlowConfig'
import { SurveyFormFields } from '@/user/survey/SurveyFormFields'
import type { SurveyFormValues } from '@/user/survey/surveyTypes'
import type { AddAccountParticipantDraft } from '../types'

const schema = yup
    .object({
        occupation: yup.string().required('Please enter occupation').min(2, 'Please enter valid occupation'),
        investmentPurpose: yup.string().required('Please select an option'),
        expectedTradeFrequency: yup.string().required('Please select an option'),
        expectedTradeAmount: yup.string().required('Please select an option'),
    })
    .required()

type Props = {
    participant: AddAccountParticipantDraft
    onSubmit: (data: SurveyFormValues) => void
    registerStepSubmitHandler: (stepId: ApplicationFlowStepId, handler: () => Promise<boolean>) => () => void
}

export function AddAccountParticipantSurveyStep({ participant, onSubmit, registerStepSubmitHandler }: Props) {
    const {
        setValue,
        trigger,
        control,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<SurveyFormValues>({
        resolver: yupResolver(schema),
        mode: 'onSubmit',
        defaultValues: {
            occupation: participant.occupation ?? '',
            investmentPurpose: participant.investmentPurpose ?? '',
            expectedTradeFrequency: participant.expectedTradeFrequency ?? '',
            expectedTradeAmount: participant.expectedTradeAmount ?? '',
        },
    })

    useEffect(() => {
        setValue('occupation', participant.occupation ?? '')
        setValue('investmentPurpose', participant.investmentPurpose ?? '')
        setValue('expectedTradeFrequency', participant.expectedTradeFrequency ?? '')
        setValue('expectedTradeAmount', participant.expectedTradeAmount ?? '')
    }, [participant, setValue])

    // Propagate survey changes to parent immediately so flow context updates
    const watchedOccupation = watch('occupation')
    const watchedInvestmentPurpose = watch('investmentPurpose')
    const watchedExpectedTradeFrequency = watch('expectedTradeFrequency')
    const watchedExpectedTradeAmount = watch('expectedTradeAmount')

    useEffect(() => {
        onSubmit({
            occupation: watchedOccupation,
            investmentPurpose: watchedInvestmentPurpose,
            expectedTradeFrequency: watchedExpectedTradeFrequency,
            expectedTradeAmount: watchedExpectedTradeAmount,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedOccupation, watchedInvestmentPurpose, watchedExpectedTradeFrequency, watchedExpectedTradeAmount])

    useEffect(() => {
        const unregister = registerStepSubmitHandler(APPLICATION_FLOW_STEP_IDS.PARTICIPANT_SURVEY, async () => {
            let submitted = false
            await handleSubmit((values) => {
                onSubmit(values)
                submitted = true
            })()
            return submitted
        })

        return unregister
    }, [handleSubmit, onSubmit, registerStepSubmitHandler])

    return (
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
                <h2 className="self-stretch justify-start text-brand-forest text-3xl font-normal font-['Serious_Sans'] leading-10 mb-0">
                    {APPLICATION_COPY.participantSurvey.title}
                </h2>
                <div className="self-stretch justify-start text-black text-base font-normal font-['Inter'] leading-6">
                    {APPLICATION_COPY.participantSurvey.description}
                </div>
            </div>
            <div className="self-stretch flex w-full flex-col justify-start items-start gap-4">
                <SurveyFormFields
                    labels={APPLICATION_COPY.participantSurvey.form}
                    control={control}
                    setValue={setValue}
                    trigger={trigger}
                    errors={errors}
                    defaultValues={{
                        occupation: participant.occupation,
                        investmentPurpose: participant.investmentPurpose,
                        expectedTradeFrequency: participant.expectedTradeFrequency,
                        expectedTradeAmount: participant.expectedTradeAmount,
                    }}
                    labelClassName="text-sm"
                />
            </div>
        </div>
    )
}
